/**
 * Booking Menu Page
 *
 * Main entry point for the booking system.
 * Displays three booking options: Lodges, Activities, and Packages.
 * Supports internationalization with cookie-based locale detection.
 */

'use client';

import Link from 'next/link';
import Image from 'next/image';
import { useTranslations } from 'next-intl';
import { lodges, adventures } from '@/lib/data';
import styles from './page.module.css';

interface BookingOption {
  id: 'lodges' | 'activities' | 'packages';
  image: string;
  href: string;
  count?: number;
  comingSoon?: boolean;
}

const bookingOptions: BookingOption[] = [
  {
    id: 'lodges',
    image: '/images/lodges/apex-mountain-penthouse/IMG_2891.webp',
    href: '/booking/lodges',
    count: lodges.length,
  },
  {
    id: 'activities',
    image: '/images/adventures/heli-skiing/heli-skiing-hero-1920.webp',
    href: '/booking/activities',
    count: adventures.length,
  },
  {
    id: 'packages',
    image: '/images/adventures/ski-touring/ski-touring-1280.webp',
    href: '/booking/packages',
    count: 5,
    comingSoon: true,
  },
];

function BookingOptionsCards() {
  const t = useTranslations('booking');

  return (
    <>
      {bookingOptions.map((option) => {
        const displayImage = option.image;
        const displayCount = option.count;

        const cardContent = (
          <>
            <div className={styles.optionImageContainer}>
              <Image
                src={displayImage}
                alt={t(`options.${option.id}.imageAlt`)}
                fill
                sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 25vw"
                className={styles.optionImage}
              />
              <div className={styles.optionImageOverlay} />
              {option.comingSoon && (
                <div className={styles.comingSoonBadge}>
                  {t('comingSoon')}
                </div>
              )}
            </div>
            <div className={styles.optionContent}>
              <div className={styles.optionBadge}>
                <span className={styles.badgeCount}>{displayCount === undefined ? '—' : displayCount}</span>
                <span className={styles.badgeLabel}>{t(`options.${option.id}.countLabel`)}</span>
              </div>
              <h2 className={styles.optionTitle}>{t(`options.${option.id}.title`)}</h2>
              <p className={styles.optionDescription}>{t(`options.${option.id}.description`)}</p>
              {!option.comingSoon && (
                <span className={styles.optionCta}>
                  {t('options.exploreOptions')}
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M5 12h14" />
                    <path d="m12 5 7 7-7 7" />
                  </svg>
                </span>
              )}
            </div>
          </>
        );

        if (option.comingSoon) {
          return (
            <div
              key={option.id}
              className={`${styles.optionCard} ${styles.optionCardDisabled}`}
            >
              {cardContent}
            </div>
          );
        }

        return (
          <Link
            key={option.id}
            href={option.href}
            className={styles.optionCard}
          >
            {cardContent}
          </Link>
        );
      })}
    </>
  );
}

export default function BookingMenuPage() {
  const t = useTranslations('booking');

  return (
    <div className={styles.bookingPage}>
      {/* Hero Section */}
      <section className={styles.hero} data-hero-background>
        <div className={styles.heroBackground}>
          <Image
            src="/images/adventures/resort-skiing/resort-skiing-hero-1280.webp"
            alt="Apex Experience luxury ski resort"
            fill
            priority
            sizes="100vw"
            className={styles.heroImage}
          />
          <div className={styles.heroOverlay} />
        </div>
        <div className={styles.heroContent}>
          <span className={styles.overline}>{t('hero.overline')}</span>
          <h1 className={styles.heroTitle}>
            {t('hero.title')}
          </h1>
          <p className={styles.heroSubtitle}>
            {t('hero.subtitle')}
          </p>
        </div>
      </section>

      {/* Booking Options */}
      <section className={styles.optionsSection}>
        <div className={styles.container}>
          <div className={styles.optionsGrid}>
            <BookingOptionsCards />
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className={styles.featuresSection}>
        <div className={styles.container}>
          <h2 className={styles.featuresTitle}>{t('features.title')}</h2>
          <div className={styles.featuresGrid}>
            <div className={styles.featureCard}>
              <div className={styles.featureIcon}>
                <svg
                  width="32"
                  height="32"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                  <path d="m9 12 2 2 4-4" />
                </svg>
              </div>
              <h3 className={styles.featureTitle}>{t('features.secureBooking.title')}</h3>
              <p className={styles.featureDescription}>
                {t('features.secureBooking.description')}
              </p>
            </div>

            <div className={styles.featureCard}>
              <div className={styles.featureIcon}>
                <svg
                  width="32"
                  height="32"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M3.85 8.62a4 4 0 0 1 4.78-4.77 4 4 0 0 1 6.74 0 4 4 0 0 1 4.78 4.78 4 4 0 0 1 0 6.74 4 4 0 0 1-4.77 4.78 4 4 0 0 1-6.75 0 4 4 0 0 1-4.78-4.77 4 4 0 0 1 0-6.76Z" />
                  <path d="m9 12 2 2 4-4" />
                </svg>
              </div>
              <h3 className={styles.featureTitle}>{t('features.instantConfirmation.title')}</h3>
              <p className={styles.featureDescription}>
                {t('features.instantConfirmation.description')}
              </p>
            </div>

            <div className={styles.featureCard}>
              <div className={styles.featureIcon}>
                <svg
                  width="32"
                  height="32"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z" />
                </svg>
              </div>
              <h3 className={styles.featureTitle}>{t('features.personalizedService.title')}</h3>
              <p className={styles.featureDescription}>
                {t('features.personalizedService.description')}
              </p>
            </div>

            <div className={styles.featureCard}>
              <div className={styles.featureIcon}>
                <svg
                  width="32"
                  height="32"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M12 8V4H8" />
                  <rect width="16" height="12" x="4" y="8" rx="2" />
                  <path d="M2 14h2" />
                  <path d="M20 14h2" />
                  <path d="M15 13v2" />
                  <path d="M9 13v2" />
                </svg>
              </div>
              <h3 className={styles.featureTitle}>{t('features.flexibleCancellation.title')}</h3>
              <p className={styles.featureDescription}>
                {t('features.flexibleCancellation.description')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className={styles.ctaSection}>
        <div className={styles.container}>
          <div className={styles.ctaContent}>
            <h2 className={styles.ctaTitle}>{t('cta.title')}</h2>
            <p className={styles.ctaDescription}>
              {t('cta.description')}
            </p>
            <div className={styles.ctaButtons}>
              <Link href="/en/contact" className={styles.ctaButtonPrimary}>
                {t('cta.contactUs')}
              </Link>
              <Link href="/en/guided-adventures" className={styles.ctaButtonSecondary}>
                {t('cta.exploreAdventures')}
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
